import { DateTime } from "luxon";

export function startLessOrEqualToEnd(
  naiveLocalTimestampStart: string,
  naiveLocalTimestampEnd: string,
): boolean {
  const start = new Date(naiveLocalTimestampStart);
  const end = new Date(naiveLocalTimestampEnd);
  return start <= end;
}

export function formatBookingDurationSeconds(seconds: number): string {
  if (seconds < 3600) return "< 1h";
  const hours = Math.floor(seconds / 3600);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(seconds / 86400);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"}`;
  const weeks = Math.floor(seconds / (7 * 86400));
  return `${weeks} week${weeks === 1 ? "" : "s"}`;
}

export function ISOStringToReadableNotLocalized(ISOTimestamp: string): string {
  // keeps the input ISO string datetime in the timezone give - not
  // changed to client's local timezone
  // Referennce https://moment.github.io/luxon/api-docs/index.html#datetimetolocalestring
  return DateTime.fromISO(ISOTimestamp.split("T")[0]).toFormat("LLL dd, yyyy");
}

export function ISOStringToMonthDayYear(ISOTimestamp: string): string {
  return DateTime.fromISO(ISOTimestamp.split("T")[0]).toFormat("MMMM d, yyyy");
}

export function ISOStringToRelativeTime(ISOTimestamp: string): string {
  return DateTime.fromISO(ISOTimestamp).toRelative() ?? "";
}

export function ISOStringToShorthandDate(ISOTimestamp: string): string {
  return DateTime.fromISO(ISOTimestamp.split("T")[0]).toFormat("dd/MM/yyyy");
}

export function ISOStringToLocalReadableTimestamp(
  ISOTimestamp: string | null | undefined,
  dateTimeFormatOpts?: Intl.DateTimeFormatOptions,
): string {
  // Reference: https://moment.github.io/luxon/api-docs/index.html#datetimetolocalestring
  // Guard null/empty/unparseable input so callers never render the literal
  // "Invalid DateTime" that luxon emits for an invalid DateTime.
  if (!ISOTimestamp) {
    return "";
  }
  const formatOpts: Intl.DateTimeFormatOptions = dateTimeFormatOpts ?? {
    weekday: "long",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    year: "numeric",
  };
  const dateTime = DateTime.fromISO(ISOTimestamp);
  return dateTime.isValid ? dateTime.toLocaleString(formatOpts) : "";
}

export function ISOStringToUnixEpoch(ISOTimestamp: string): number {
  const datetime = DateTime.fromISO(ISOTimestamp);
  return datetime.toUnixInteger();
}

export function isWithinLastMinutes(
  ISOTimestamp: string,
  minutes: number,
): boolean {
  const dateTime = DateTime.fromISO(ISOTimestamp);
  if (!dateTime.isValid) return false;
  return DateTime.now().diff(dateTime, "minutes").minutes <= minutes;
}

// Null rather than NaN on an unparseable timestamp: callers report this as an
// analytics property, and NaN serializes to null anyway but only after passing
// through arithmetic that hides where it came from.
export function secondsSince(ISOTimestamp: string): number | null {
  const dateTime = DateTime.fromISO(ISOTimestamp);
  if (!dateTime.isValid) return null;
  return Math.floor(DateTime.now().diff(dateTime, "seconds").seconds);
}

export function sortByOldest(
  quotes: ShipperCondensedQuote[],
): ShipperCondensedQuote[] {
  quotes.sort(
    (a, b) =>
      ISOStringToUnixEpoch(a.created_at) - ISOStringToUnixEpoch(b.created_at),
  );
  return quotes;
}

export function ISOStringToReadableDate(ISOTimestamp: string): string {
  // Reference: https://moment.github.io/luxon/api-docs/index.html#datetimetolocalestring
  const formatOpts: Intl.DateTimeFormatOptions = {
    weekday: "long",
    month: "short",
    day: "2-digit",
    year: "numeric",
  };
  return DateTime.fromISO(ISOTimestamp.split("T")[0]).toLocaleString(
    formatOpts,
  );
}

export function naiveDateToISOString(naiveDate: string): string {
  return new Date(naiveDate).toISOString();
}

export function extractDateFromIsoString(ISOTimestamp: string): string {
  const parts = ISOTimestamp.split("T");
  if (parts.length < 2) {
    throw new Error("Invalid format: 'T' not found in the string");
  }
  return parts[0];
}

/**
 * How long ago something last moved, for a compact label beside an action.
 *
 * Short by design — it sits under a set of buttons and has to read at a glance,
 * so "12m" rather than "12 minutes ago". Past a week a relative figure stops
 * being useful and an actual date says more, so it hands over to one.
 */
export function formatQuoteAge(ISOTimestamp: string): string {
  const then = DateTime.fromISO(ISOTimestamp);
  if (!then.isValid) {
    return "";
  }

  const elapsed = DateTime.now().diff(then);
  const minutes = Math.floor(elapsed.as("minutes"));
  // A clock skewed slightly ahead of the server would otherwise read "-1m".
  if (minutes < 1) {
    return "now";
  }
  if (minutes < 60) {
    return `${minutes}m`;
  }

  const hours = Math.floor(elapsed.as("hours"));
  if (hours < 24) {
    return `${hours}h`;
  }

  const days = Math.floor(elapsed.as("days"));
  if (days < 7) {
    return `${days}d`;
  }

  return then.toFormat("LLL d");
}

/**
 * The full timestamp behind {@link formatQuoteAge}, for its tooltip. "12m" on
 * its own does not say what moved, and a countered quote's age is the counter's.
 */
export function formatQuoteAgeTooltip(
  ISOTimestamp: string,
  wasCountered: boolean,
): string {
  const then = DateTime.fromISO(ISOTimestamp);
  if (!then.isValid) {
    return "";
  }

  const label = wasCountered ? "Counter received" : "Quote received";
  return `${label} ${then.toFormat("LLL d, yyyy, h:mm a")}`;
}
